import { useState, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Search, Loader2, UserSearch } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  createUserSchema,
  type CreateUserFormData,
} from "../schemas/userFormSchema";
import { useUserStore } from "../store";
import type { NetSuiteEmployeeDto } from "../types";

interface CreateUserFormProps {
  onSubmit: (data: CreateUserFormData) => void;
  onCancel: () => void;
}

export function CreateUserForm({ onSubmit, onCancel }: CreateUserFormProps) {
  const [useNetSuiteLookup, setUseNetSuiteLookup] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  const {
    employeeSearchResults,
    isSearchingEmployees,
    employeeSearchError,
    searchEmployees,
    clearEmployeeSearch,
  } = useUserStore();

  const form = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      username: "",
      email: "",
      firstName: "",
      middleName: "",
      lastName: "",
      employeeIdNumber: "",
      mobileNumber: "",
      startDate: null,
      birthdate: null,
      departmentId: null,
      departmentName: null,
      positionId: null,
      positionName: null,
      isActive: true,
      netSuiteEmployeeId: null,
    },
  });

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchTerm(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        searchEmployees(value);
      }, 400);
    },
    [searchEmployees]
  );

  const generateUsername = (
    firstName: string,
    lastName: string,
    birthdate: string | null
  ): string => {
    const cleanFirst = firstName.toLowerCase().replace(/[^a-z]/g, "");
    const lastInitial = lastName.charAt(0).toLowerCase().replace(/[^a-z]/g, "");
    const day = birthdate
      ? new Date(birthdate).getUTCDate().toString().padStart(2, "0")
      : "";
    return `csc_${cleanFirst}${lastInitial}${day}`;
  };

  const handleSelectEmployee = (employee: NetSuiteEmployeeDto) => {
    form.setValue("firstName", employee.firstName || "");
    form.setValue("middleName", employee.middleName || "");
    form.setValue("lastName", employee.lastName || "");
    form.setValue("email", employee.email || "");
    form.setValue("employeeIdNumber", employee.employeeIdNumber || "");
    form.setValue("startDate", employee.hireDate || null);
    form.setValue("birthdate", employee.birthdate || null);
    form.setValue("departmentId", employee.departmentId || null);
    form.setValue("departmentName", employee.departmentName || null);
    form.setValue("positionId", employee.positionId || null);
    form.setValue("positionName", employee.positionName || null);
    form.setValue("netSuiteEmployeeId", employee.id);

    if (employee.firstName && employee.lastName) {
      form.setValue(
        "username",
        generateUsername(employee.firstName, employee.lastName, employee.birthdate)
      );
    }

    setSearchTerm("");
    clearEmployeeSearch();
  };

  const handleToggleLookup = (checked: boolean) => {
    setUseNetSuiteLookup(checked);
    if (!checked) {
      setSearchTerm("");
      clearEmployeeSearch();
      form.setValue("netSuiteEmployeeId", null);
    }
  };

  const handleSubmit = (data: CreateUserFormData) => {
    onSubmit(data);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="space-y-6"
      >
        {/* NetSuite Lookup Toggle */}
        <div className="flex flex-row items-center justify-between rounded-lg border border-info/30 bg-info/10 p-3">
          <div className="flex items-center gap-2">
            <UserSearch className="h-4 w-4 text-info" />
            <div className="space-y-0.5">
              <p className="text-sm font-medium">NetSuite Employee Lookup</p>
              <p className="text-xs text-muted-foreground">
                Search and auto-fill from NetSuite
              </p>
            </div>
          </div>
          <Switch
            checked={useNetSuiteLookup}
            onCheckedChange={handleToggleLookup}
          />
        </div>

        {/* NetSuite Employee Search */}
        {useNetSuiteLookup && (
          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search employee by name or ID..."
                className="pl-9"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
              {isSearchingEmployees && (
                <Loader2 className="absolute right-3 top-[calc(50%-0.5rem)] h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>

            {employeeSearchError && (
              <p className="text-sm text-destructive">{employeeSearchError}</p>
            )}

            {employeeSearchResults.length > 0 && (
              <div className="max-h-48 overflow-y-auto rounded-md border">
                {employeeSearchResults.map((emp) => (
                  <button
                    key={emp.id}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-accent text-sm border-b last:border-b-0 transition-colors"
                    onClick={() => handleSelectEmployee(emp)}
                  >
                    <div className="font-medium">
                      {emp.lastName}, {emp.firstName}
                      {emp.middleName ? ` ${emp.middleName}` : ""}
                    </div>
                    <div className="text-xs text-muted-foreground flex gap-3">
                      {emp.employeeIdNumber && <span>{emp.employeeIdNumber}</span>}
                      {emp.positionName && <span>{emp.positionName}</span>}
                      {emp.departmentName && <span>{emp.departmentName}</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {searchTerm.length >= 2 &&
              !isSearchingEmployees &&
              employeeSearchResults.length === 0 &&
              !employeeSearchError && (
                <p className="text-sm text-muted-foreground">
                  No employees found
                </p>
              )}
          </div>
        )}

        <Separator />

        {/* Account Information Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-muted-foreground">
            Account Information
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Username *</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter username" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email *</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="Enter email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <p className="text-xs text-muted-foreground">
            A temporary password will be assigned automatically. The user must change it on first login.
          </p>
        </div>

        <Separator />

        {/* Personal Information Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-muted-foreground">
            Personal Information
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="First name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="middleName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Middle Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Middle name (optional)"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Last name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="birthdate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Birthdate</FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) =>
                        field.onChange(e.target.value || null)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="startDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Start Date</FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) =>
                        field.onChange(e.target.value || null)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <Separator />

        {/* Employment Information Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-muted-foreground">
            Employment Information
          </h4>

          <FormField
            control={form.control}
            name="employeeIdNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Employee ID *</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Employee ID"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="departmentName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Department</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Department"
                      readOnly={useNetSuiteLookup}
                      className={useNetSuiteLookup ? "bg-muted" : ""}
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) =>
                        field.onChange(e.target.value || null)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="positionName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Position</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Position"
                      readOnly={useNetSuiteLookup}
                      className={useNetSuiteLookup ? "bg-muted" : ""}
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) =>
                        field.onChange(e.target.value || null)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="mobileNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mobile Number</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Mobile number (optional)"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Separator />

        {/* Status Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-muted-foreground">
            Status
          </h4>

          <FormField
            control={form.control}
            name="isActive"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                <div className="space-y-0.5">
                  <FormLabel>Active Status</FormLabel>
                  <FormDescription>
                    User can log in when active
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <Separator />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" isLoading={form.formState.isSubmitting}>
            Create User
          </Button>
        </div>
      </form>
    </Form>
  );
}
